<script lang="ts">
  import { onMount } from "svelte";
  import { push, link } from "svelte-spa-router";
  import {
    listSchools,
    listAuthorities,
    listRooms,
    createRoom,
    deleteRoom,
    updateSchool,
    listUsers,
    createUser,
    addUserRole,
    removeUserRole,
    deleteUser,
    listDevices,
    setDeviceRoom,
    ApiError,
  } from "../lib/api";
  import Breadcrumb from "../lib/Breadcrumb.svelte";
  import UserLink from "../lib/UserLink.svelte";
  import { scopes } from "../lib/session.svelte";
  import type {
    AdminRoom,
    AdminUser,
    Authority,
    DeviceListEntry,
    School,
  } from "@diy-avatar/shared/admin";

  export let params: { id?: string } = {};
  $: schoolId = params.id ? parseInt(params.id, 10) : NaN;

  type Tab = "rooms" | "teachers" | "students" | "devices";
  let tab: Tab = "rooms";

  let school: School | null = null;
  /** Parent authority's forbid flag, for the inherited indicator. */
  let authorityForbids = false;
  let authorityForcesQr = false;
  let authorityForcesE2ee = false;
  let rooms: AdminRoom[] = [];
  let users: AdminUser[] = [];
  let devices: DeviceListEntry[] = [];
  let loading = false;
  let error: string | null = null;

  // forms
  let newRoomName = "";
  let newRoomVisibility: "public" | "restricted" = "public";
  let newRoomDescription = "";

  let newTeacherEmail = "";
  let newTeacherPassword = "";
  let newStudentEmail = "";
  let newStudentPassword = "";

  $: canManage =
    $scopes.isAdmin || $scopes.manageableSchoolIds.includes(schoolId);

  function handleAuth(err: unknown): boolean {
    if (err instanceof ApiError && err.status === 401) {
      push("/");
      return true;
    }
    return false;
  }

  function errText(err: unknown, fallback: string): string {
    if (err instanceof ApiError) {
      if (err.message === "last_school_manager") {
        return "Cannot remove the last school-manager of this school.";
      }
      return err.message;
    }
    return err instanceof Error ? err.message : fallback;
  }

  async function load(id: number) {
    if (!Number.isFinite(id)) return;
    loading = true;
    try {
      const [sc, au, rm, us, dv] = await Promise.all([
        listSchools(),
        listAuthorities().catch(() => ({ authorities: [] as Authority[] })),
        listRooms(),
        listUsers().catch(() => ({ users: [] as AdminUser[] })),
        listDevices().catch(() => ({ devices: [] as DeviceListEntry[] })),
      ]);
      school = sc.schools.find((s) => s.id === id) ?? null;
      authorityForbids =
        au.authorities.find((a) => a.id === school?.authority_id)
          ?.forbid_invisible ?? false;
      authorityForcesQr =
        au.authorities.find((a) => a.id === school?.authority_id)
          ?.force_e2ee_qr ?? false;
      authorityForcesE2ee =
        au.authorities.find((a) => a.id === school?.authority_id)
          ?.force_e2ee ?? false;
      rooms = rm.rooms.filter((r) => r.school_id === id);
      // Users that hold any role scoped to this school.
      users = us.users.filter((u) =>
        u.roles.some((r) => r.school_id === id),
      );
      const roomIds = new Set(rooms.map((r) => r.id));
      devices = dv.devices.filter((d) => d.room_id != null && roomIds.has(d.room_id));
      error = null;
    } catch (err) {
      if (handleAuth(err)) return;
      error = errText(err, "Could not load school.");
    } finally {
      loading = false;
    }
  }

  async function toggleForbidInvisible(checked: boolean) {
    if (!school) return;
    error = null;
    try {
      await updateSchool(school.id, { forbid_invisible: checked });
      await load(schoolId);
    } catch (err) {
      if (handleAuth(err)) return;
      error = errText(err, "save failed");
    }
  }

  async function toggleForceE2eeQr(checked: boolean) {
    if (!school) return;
    error = null;
    try {
      await updateSchool(school.id, { force_e2ee_qr: checked });
      await load(schoolId);
    } catch (err) {
      if (handleAuth(err)) return;
      error = errText(err, "save failed");
    }
  }

  async function toggleForceE2ee(checked: boolean) {
    if (!school) return;
    error = null;
    try {
      await updateSchool(school.id, { force_e2ee: checked });
      await load(schoolId);
    } catch (err) {
      if (handleAuth(err)) return;
      error = errText(err, "save failed");
    }
  }

  async function setRotateHour(raw: string) {
    if (!school) return;
    error = null;
    const hour = raw === "" ? null : parseInt(raw, 10);
    if (hour !== null && (!Number.isInteger(hour) || hour < 0 || hour > 23)) {
      error = "rotation hour must be 0-23";
      return;
    }
    try {
      await updateSchool(school.id, { e2ee_rotate_hour: hour });
      await load(schoolId);
    } catch (err) {
      if (handleAuth(err)) return;
      error = errText(err, "save failed");
    }
  }

  // ---- rooms ----
  async function createRoomSubmit() {
    error = null;
    if (!newRoomName.trim()) {
      error = "room name required";
      return;
    }
    try {
      await createRoom({
        name: newRoomName.trim(),
        school_id: schoolId,
        visibility: newRoomVisibility,
        description: newRoomDescription.trim() || null,
      });
      newRoomName = "";
      newRoomDescription = "";
      newRoomVisibility = "public";
      await load(schoolId);
    } catch (err) {
      if (handleAuth(err)) return;
      error = errText(err, "create room failed");
    }
  }

  async function removeRoom(r: AdminRoom) {
    if (!confirm(`Delete room "${r.name}"?`)) return;
    try {
      await deleteRoom(r.id);
      await load(schoolId);
    } catch (err) {
      if (handleAuth(err)) return;
      error = errText(err, "delete room failed");
    }
  }

  // ---- users ----
  function teachers(): AdminUser[] {
    return users.filter((u) =>
      u.roles.some((r) => r.role === "teacher" && r.school_id === schoolId),
    );
  }
  function students(): AdminUser[] {
    return users.filter((u) =>
      u.roles.some((r) => r.role === "student" && r.school_id === schoolId),
    );
  }
  function isManager(u: AdminUser): boolean {
    return u.roles.some(
      (r) => r.role === "school-manager" && r.school_id === schoolId,
    );
  }
  function managerRoleId(u: AdminUser): number | null {
    return (
      u.roles.find((r) => r.role === "school-manager" && r.school_id === schoolId)
        ?.id ?? null
    );
  }

  async function createTeacher() {
    error = null;
    if (!newTeacherEmail.trim() || !newTeacherPassword) {
      error = "email and password required";
      return;
    }
    try {
      await createUser({
        email: newTeacherEmail.trim(),
        password: newTeacherPassword,
        roles: [{ role: "teacher", school_id: schoolId }],
        skip_verification: true,
      });
      newTeacherEmail = "";
      newTeacherPassword = "";
      await load(schoolId);
    } catch (err) {
      if (handleAuth(err)) return;
      error = errText(err, "create teacher failed");
    }
  }

  async function createStudent() {
    error = null;
    if (!newStudentEmail.trim() || !newStudentPassword) {
      error = "email and password required";
      return;
    }
    try {
      await createUser({
        email: newStudentEmail.trim(),
        password: newStudentPassword,
        roles: [{ role: "student", school_id: schoolId }],
        skip_verification: true,
      });
      newStudentEmail = "";
      newStudentPassword = "";
      await load(schoolId);
    } catch (err) {
      if (handleAuth(err)) return;
      error = errText(err, "create student failed");
    }
  }

  async function toggleManager(u: AdminUser) {
    error = null;
    try {
      if (isManager(u)) {
        const rid = managerRoleId(u);
        if (rid != null) await removeUserRole(u.id, rid);
      } else {
        await addUserRole(u.id, { role: "school-manager", school_id: schoolId });
      }
      await load(schoolId);
    } catch (err) {
      if (handleAuth(err)) return;
      error = errText(err, "role change failed");
    }
  }

  async function removeUser(u: AdminUser) {
    if (!confirm(`Delete user ${u.email}?`)) return;
    try {
      await deleteUser(u.id);
      await load(schoolId);
    } catch (err) {
      if (handleAuth(err)) return;
      error = errText(err, "delete failed");
    }
  }

  // ---- devices ----
  async function assignRoom(d: DeviceListEntry, roomId: number | null) {
    error = null;
    try {
      await setDeviceRoom(d.label, roomId);
      await load(schoolId);
    } catch (err) {
      if (handleAuth(err)) return;
      error = errText(err, "assign room failed");
    }
  }

  $: if (Number.isFinite(schoolId)) void load(schoolId);
  onMount(() => {});
</script>

<section>
  <Breadcrumb
    items={[
      { label: "Schools", href: "/schools" },
      { label: school?.name ?? "…" },
    ]}
  />
  <h1>{school?.name ?? "School"}</h1>
  {#if school?.authority_name}
    <p class="meta">
      Authority:
      {#if $scopes.isAdmin}
        <a href="/authorities/{school.authority_id}" use:link>{school.authority_name}</a>
      {:else}
        {school.authority_name}
      {/if}
      {#if school.description}· {school.description}{/if}
    </p>
  {/if}
  {#if error}<p class="error" data-testid="error">{error}</p>{/if}

  {#if school}
    <div class="new" data-testid="school-settings">
      <h2>Settings</h2>
      <label class="toggle">
        <input
          type="checkbox"
          checked={school.forbid_invisible}
          disabled={!canManage}
          on:change={(e) => toggleForbidInvisible(e.currentTarget.checked)}
          data-testid="forbid-invisible"
        />
        Forbid invisible mode
      </label>
      {#if authorityForbids}
        <span class="inherited" data-testid="forbid-invisible-inherited">
          Already forbidden by authority{school.authority_name
            ? ` (${school.authority_name})`
            : ""}
        </span>
      {/if}
      <p class="hint">
        When enabled, students in this school may not use invisible mode.
      </p>
      <label class="toggle">
        <input
          type="checkbox"
          checked={school.force_e2ee_qr}
          disabled={!canManage}
          on:change={(e) => toggleForceE2eeQr(e.currentTarget.checked)}
          data-testid="force-e2ee-qr"
        />
        QR-Schlüsselaustausch erzwingen
      </label>
      {#if authorityForcesQr}
        <span class="inherited" data-testid="force-e2ee-qr-inherited">
          Bereits erzwungen durch Schulamt{school.authority_name
            ? ` (${school.authority_name})`
            : ""}
        </span>
      {/if}
      <p class="hint e2ee-qr-desc">
        <strong>QR-Schlüsselaustausch erzwingen.</strong> Wenn aktiv, müssen
        Schüler:innen den QR-Code am Klassen-Display <strong>persönlich
        scannen</strong>. Der Schlüssel verlässt dabei nie unsere Server —
        höchste Sicherheit, aber alle müssen physisch anwesend sein. Wenn
        deaktiviert, werden Schlüssel automatisch verteilt (für jedes
        Schüler-Gerät versiegelt), sodass auch entfernte Schüler:innen ohne
        Scannen verschlüsselt teilnehmen — das vertraut darauf, dass der Server
        korrekte öffentliche Schlüssel ausliefert. Aktivierung auf
        <strong>einer beliebigen Ebene</strong> (Schulamt/Schule/Raum/Gerät)
        erzwingt QR für <strong>alle</strong> darunterliegenden Geräte und
        Schüler:innen.
      </p>
      <label class="toggle">
        <input
          type="checkbox"
          checked={school.force_e2ee}
          disabled={!canManage}
          on:change={(e) => toggleForceE2ee(e.currentTarget.checked)}
          data-testid="force-e2ee"
        />
        Verschlüsselung erzwingen
      </label>
      {#if authorityForcesE2ee}
        <span class="inherited" data-testid="force-e2ee-inherited">
          Bereits erzwungen durch Schulamt{school.authority_name
            ? ` (${school.authority_name})`
            : ""}
        </span>
      {/if}
      <p class="hint e2ee-desc">
        <strong>Verschlüsselung erzwingen.</strong> Wenn aktiv, ist
        Ende-zu-Ende-Verschlüsselung für alle Geräte und Schüler:innen darunter
        verpflichtend (kein Opt-out, nicht unterstützte Browser werden
        blockiert). Schlüssel werden weiterhin <strong>automatisch verteilt</strong>
        (für jedes Schüler-Gerät versiegelt) — anders als „QR erzwingen“, das die
        automatische Verteilung abschaltet. Aktivierung auf
        <strong>einer beliebigen Ebene</strong> (Schulamt/Schule/Raum/Gerät)
        erzwingt Verschlüsselung für <strong>alle</strong> darunterliegenden
        Geräte und Schüler:innen.
      </p>
      <label class="field">
        <span>Tägliche E2EE-Schlüsselrotation (Stunde, Ortszeit)</span>
        <select
          value={school.e2ee_rotate_hour === null
            ? ""
            : String(school.e2ee_rotate_hour)}
          disabled={!canManage}
          on:change={(e) => setRotateHour(e.currentTarget.value)}
          data-testid="e2ee-rotate-hour"
        >
          <option value="">Vererbt (Standard)</option>
          {#each Array(24) as _, h}
            <option value={String(h)}>{String(h).padStart(2, "0")}:00</option>
          {/each}
        </select>
      </label>
      <p class="hint">
        Stunde (0-23, Ortszeit), zu der der E2EE-Medienschlüssel täglich rotiert
        wird. Leer = von der übergeordneten Ebene erben (Standard 00:00).
      </p>
    </div>
  {/if}

  <div class="tabs" data-testid="school-tabs">
    <button class:active={tab === "rooms"} on:click={() => (tab = "rooms")} data-testid="tab-rooms">
      Rooms ({rooms.length})
    </button>
    <button class:active={tab === "teachers"} on:click={() => (tab = "teachers")} data-testid="tab-teachers">
      Teachers ({teachers().length})
    </button>
    <button class:active={tab === "students"} on:click={() => (tab = "students")} data-testid="tab-students">
      Students ({students().length})
    </button>
    <button class:active={tab === "devices"} on:click={() => (tab = "devices")} data-testid="tab-devices">
      Devices ({devices.length})
    </button>
  </div>

  {#if tab === "rooms"}
    <div class="pane" data-testid="pane-rooms">
      {#if canManage}
        <div class="new">
          <h2>Create room</h2>
          <p class="hint">
            A room may be a class name — it need not be a physical room.
          </p>
          <div class="row">
            <input type="text" placeholder="name / class" bind:value={newRoomName} data-testid="new-room-name" />
            <select bind:value={newRoomVisibility} data-testid="new-room-visibility">
              <option value="public">public</option>
              <option value="restricted">restricted</option>
            </select>
            <input type="text" placeholder="description (optional)" bind:value={newRoomDescription} />
            <button on:click={createRoomSubmit} data-testid="new-room-submit">Create</button>
          </div>
        </div>
      {/if}
      <table data-testid="rooms-table">
        <thead>
          <tr><th>id</th><th>name</th><th>visibility</th><th>devices</th><th>online</th><th></th></tr>
        </thead>
        <tbody>
          {#each rooms as r (r.id)}
            <tr data-testid="room-row-{r.id}">
              <td>#{r.id}</td>
              <td><a href="/rooms/{r.id}" use:link data-testid="room-link-{r.id}">{r.name}</a></td>
              <td>{r.visibility}</td>
              <td>{r.devices.length ? r.devices.join(", ") : "—"}</td>
              <td><span class="dot" class:online={r.online}></span></td>
              <td class="actions">
                {#if canManage}<button on:click={() => removeRoom(r)}>delete</button>{/if}
              </td>
            </tr>
          {/each}
          {#if rooms.length === 0 && !loading}
            <tr><td colspan="6" class="empty">No rooms.</td></tr>
          {/if}
        </tbody>
      </table>
    </div>
  {:else if tab === "teachers"}
    <div class="pane" data-testid="pane-teachers">
      {#if canManage}
        <div class="new">
          <h2>Add teacher</h2>
          <div class="row">
            <input type="email" placeholder="email" bind:value={newTeacherEmail} data-testid="new-teacher-email" />
            <input type="text" placeholder="password" bind:value={newTeacherPassword} data-testid="new-teacher-password" />
            <button on:click={createTeacher} data-testid="new-teacher-submit">Create</button>
          </div>
        </div>
      {/if}
      <table data-testid="teachers-table">
        <thead>
          <tr><th>id</th><th>email</th><th>manager?</th><th></th></tr>
        </thead>
        <tbody>
          {#each teachers() as u (u.id)}
            <tr data-testid="teacher-row-{u.id}">
              <td>#{u.id}</td>
              <td>
                <UserLink
                  id={u.id}
                  email={u.email}
                  fullName={u.full_name}
                  displayName={u.display_name}
                  hasImage={u.has_profile_image}
                  size={24}
                />
              </td>
              <td>
                <span class:badge={isManager(u)}>{isManager(u) ? "school-manager" : "—"}</span>
              </td>
              <td class="actions">
                {#if canManage}
                  <button on:click={() => toggleManager(u)} data-testid="toggle-manager-{u.id}">
                    {isManager(u) ? "Demote" : "Make manager"}
                  </button>
                  <button on:click={() => removeUser(u)}>delete</button>
                {/if}
              </td>
            </tr>
          {/each}
          {#if teachers().length === 0 && !loading}
            <tr><td colspan="4" class="empty">No teachers.</td></tr>
          {/if}
        </tbody>
      </table>
    </div>
  {:else if tab === "students"}
    <div class="pane" data-testid="pane-students">
      {#if canManage}
        <div class="new">
          <h2>Add student</h2>
          <div class="row">
            <input type="email" placeholder="email" bind:value={newStudentEmail} data-testid="new-student-email" />
            <input type="text" placeholder="password" bind:value={newStudentPassword} data-testid="new-student-password" />
            <button on:click={createStudent} data-testid="new-student-submit">Create</button>
          </div>
        </div>
      {/if}
      <table data-testid="students-table">
        <thead>
          <tr><th>id</th><th>email</th><th></th></tr>
        </thead>
        <tbody>
          {#each students() as u (u.id)}
            <tr data-testid="student-row-{u.id}">
              <td>#{u.id}</td>
              <td>
                <UserLink
                  id={u.id}
                  email={u.email}
                  fullName={u.full_name}
                  displayName={u.display_name}
                  hasImage={u.has_profile_image}
                  size={24}
                />
              </td>
              <td class="actions">
                {#if canManage}<button on:click={() => removeUser(u)}>delete</button>{/if}
              </td>
            </tr>
          {/each}
          {#if students().length === 0 && !loading}
            <tr><td colspan="3" class="empty">No students.</td></tr>
          {/if}
        </tbody>
      </table>
    </div>
  {:else}
    <div class="pane" data-testid="pane-devices">
      <p class="hint">Devices assigned to a room in this school.</p>
      <table data-testid="devices-table">
        <thead>
          <tr><th>id</th><th>label</th><th>room</th><th>online</th><th></th></tr>
        </thead>
        <tbody>
          {#each devices as d (d.label)}
            <tr data-testid="device-row-{d.label}">
              <td>{d.label}</td>
              <td>
                <a href="/devices/{encodeURIComponent(d.label)}" use:link>{d.label}</a>
              </td>
              <td>
                {#if canManage}
                  <select
                    value={d.room_id ?? ""}
                    on:change={(e) =>
                      assignRoom(d, e.currentTarget.value === "" ? null : Number(e.currentTarget.value))}
                    data-testid="device-room-{d.label}"
                  >
                    <option value="">— unassigned —</option>
                    {#each rooms as r (r.id)}
                      <option value={r.id}>{r.name}</option>
                    {/each}
                  </select>
                {:else}
                  {d.room_name ?? "—"}
                {/if}
              </td>
              <td><span class="dot" class:online={d.online}></span></td>
              <td><a href="/devices/{encodeURIComponent(d.label)}" use:link>Open →</a></td>
            </tr>
          {/each}
          {#if devices.length === 0 && !loading}
            <tr><td colspan="5" class="empty">No devices assigned to this school's rooms.</td></tr>
          {/if}
        </tbody>
      </table>
    </div>
  {/if}
</section>

<style>
  h1 { margin: 0 0 0.3rem; }
  h2 { margin: 0 0 0.4rem; font-size: 1rem; }
  .meta { color: #9ca3af; font-size: 0.85rem; margin: 0 0 0.75rem; }
  .meta a { color: #34d399; text-decoration: none; }
  .hint { color: #9ca3af; font-size: 0.8rem; margin: 0 0 0.5rem; }
  .e2ee-qr-desc {
    color: #d1d5db;
    background: #1c1407;
    border-left: 3px solid #b45309;
    padding: 0.6rem 0.8rem;
    border-radius: 4px;
    line-height: 1.4;
  }
  .e2ee-qr-desc strong { color: #fbbf24; }
  .tabs { display: flex; gap: 0.25rem; margin: 0.5rem 0 1rem; flex-wrap: wrap; }
  .tabs button {
    padding: 0.4rem 0.8rem;
    background: #111827;
    color: #9ca3af;
    border: 1px solid #1f2937;
    border-radius: 6px;
    cursor: pointer;
  }
  .tabs button.active { background: #1f2937; color: #f3f4f6; border-color: #374151; }
  .new {
    margin-bottom: 1rem;
    padding: 0.75rem 1rem;
    background: #111827;
    border: 1px solid #1f2937;
    border-radius: 8px;
  }
  .row { display: flex; gap: 0.5rem; flex-wrap: wrap; }
  .toggle {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    color: #d1d5db;
    font-size: 0.85rem;
  }
  .toggle input { padding: 0; }
  .field {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    color: #d1d5db;
    font-size: 0.85rem;
    margin: 0.5rem 0 0;
  }
  .field select { max-width: 16rem; }
  .inherited {
    margin-left: 0.6rem;
    color: #fbbf24;
    font-size: 0.78rem;
  }
  input, select {
    padding: 0.4rem 0.5rem;
    background: #0f172a;
    color: #f3f4f6;
    border: 1px solid #1f2937;
    border-radius: 4px;
  }
  button {
    padding: 0.4rem 0.7rem;
    background: #1f2937;
    color: #f3f4f6;
    border: 1px solid #374151;
    border-radius: 4px;
  }
  table { width: 100%; border-collapse: collapse; background: #111827; }
  th, td {
    padding: 0.4rem 0.6rem;
    text-align: left;
    border-bottom: 1px solid #1f2937;
    font-size: 0.85rem;
  }
  th { background: #0f172a; color: #9ca3af; }
  td a { color: #34d399; text-decoration: none; }
  td a:hover { text-decoration: underline; }
  .actions { display: flex; gap: 0.3rem; flex-wrap: wrap; }
  .dot {
    display: inline-block;
    width: 0.6rem;
    height: 0.6rem;
    border-radius: 50%;
    background: #6b7280;
  }
  .dot.online { background: #10b981; }
  .badge {
    background: #064e3b;
    color: #6ee7b7;
    padding: 0.1rem 0.4rem;
    border-radius: 4px;
    font-size: 0.75rem;
  }
  .empty { color: #6b7280; text-align: center; }
  .error { color: #fca5a5; }
</style>
